import type {
  Procedure,
  ProcedureSchedule,
  SchedulingResource,
} from '@/types/cgt'

import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import { useMockApi } from '@/lib/useMock'
import {
  addBlock,
  annotateConflicts,
  applyEdit,
  applyMove,
  buildSchedule,
  removeBlock,
  type BlockEdit,
  type BlockMove,
  type NewBlockInput,
} from '@/lib/scheduling'
import {
  mockProcedureAnchors,
  mockProcedures,
  mockProcedureTemplate,
  mockProcedureResources,
  mockProcedureStepTemplates,
} from '@/mocks/procedureScheduling'

// ─── Procedure scheduling API ──────────────────────────────────────────────────


async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(text || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

const SCHEDULING_BACKEND_READY = false
const useMockScheduling = !SCHEDULING_BACKEND_READY || useMockApi

// ─── Mock state ─────────────────────────────────────────────────────────────────

const mockScheduleStore = new Map<string, ProcedureSchedule>()
const mockResourceState: SchedulingResource[] = mockProcedureResources.map((r) => ({ ...r }))

function buildMockSchedule(procedureId: string): ProcedureSchedule {
  const procedure = mockProcedures.find((t) => t.id === procedureId)
  if (!procedure) throw new Error(`Mock procedure ${procedureId} not found`)
  return buildSchedule({
    procedure,
    procedureTemplate: mockProcedureTemplate,
    templates: mockProcedureStepTemplates,
    resources: mockResourceState,
    anchorDate: mockProcedureAnchors[procedureId],
  })
}

function getMockSchedule(procedureId: string): ProcedureSchedule {
  const existing = mockScheduleStore.get(procedureId)
  if (existing) return existing
  const fresh = buildMockSchedule(procedureId)
  mockScheduleStore.set(procedureId, fresh)
  return fresh
}

export const procedureSchedulingApi = {
  listUpcoming: (): Promise<Procedure[]> => {
    if (useMockScheduling) return Promise.resolve(mockProcedures)
    return request<Procedure[]>('/scheduling/procedures?status=in_progress&upcoming=true')
  },

  listResources: (): Promise<SchedulingResource[]> => {
    if (useMockScheduling) return Promise.resolve(mockResourceState)
    return request<SchedulingResource[]>('/scheduling/resources')
  },

  getSchedule: (procedureId: string): Promise<ProcedureSchedule> => {
    if (useMockScheduling) return Promise.resolve(getMockSchedule(procedureId))
    return request<ProcedureSchedule>(`/scheduling/procedures/${procedureId}/schedule`)
  },

  updateBlock: (
    procedureId: string,
    blockId: string,
    move: Omit<BlockMove, 'blockId'>,
  ): Promise<ProcedureSchedule> => {
    if (useMockScheduling) {
      const current = getMockSchedule(procedureId)
      const next = applyMove(current, mockResourceState, { blockId, ...move })
      mockScheduleStore.set(procedureId, next)
      return Promise.resolve(next)
    }
    return request<ProcedureSchedule>(
      `/scheduling/procedures/${procedureId}/schedule/blocks/${blockId}`,
      { method: 'PATCH', body: JSON.stringify(move) },
    )
  },

  editBlock: (
    procedureId: string,
    blockId: string,
    patch: Omit<BlockEdit, 'blockId'>,
  ): Promise<ProcedureSchedule> => {
    if (useMockScheduling) {
      const current = getMockSchedule(procedureId)
      const next = applyEdit(current, mockResourceState, { blockId, ...patch })
      mockScheduleStore.set(procedureId, next)
      return Promise.resolve(next)
    }
    return request<ProcedureSchedule>(
      `/scheduling/procedures/${procedureId}/schedule/blocks/${blockId}`,
      { method: 'PUT', body: JSON.stringify(patch) },
    )
  },

  createBlock: (
    procedureId: string,
    input: NewBlockInput,
  ): Promise<ProcedureSchedule> => {
    if (useMockScheduling) {
      const current = getMockSchedule(procedureId)
      const blockId = `${procedureId}:adhoc-${crypto.randomUUID()}`
      const next = addBlock(current, mockResourceState, input, blockId)
      mockScheduleStore.set(procedureId, next)
      return Promise.resolve(next)
    }
    return request<ProcedureSchedule>(
      `/scheduling/procedures/${procedureId}/schedule/blocks`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  },

  deleteBlock: (
    procedureId: string,
    blockId: string,
  ): Promise<ProcedureSchedule> => {
    if (useMockScheduling) {
      const current = getMockSchedule(procedureId)
      const next = removeBlock(current, mockResourceState, blockId)
      mockScheduleStore.set(procedureId, next)
      return Promise.resolve(next)
    }
    return request<ProcedureSchedule>(
      `/scheduling/procedures/${procedureId}/schedule/blocks/${blockId}`,
      { method: 'DELETE' },
    )
  },

  editResource: (
    resourceId: string,
    patch: Partial<Pick<SchedulingResource, 'name' | 'kind' | 'capacityHoursPerDay'>>,
  ): Promise<SchedulingResource[]> => {
    if (useMockScheduling) {
      const index = mockResourceState.findIndex((r) => r.id === resourceId)
      if (index === -1) {
        return Promise.reject(new Error(`Mock resource ${resourceId} not found`))
      }
      mockResourceState[index] = { ...mockResourceState[index], ...patch }
      // Capacity / identity changes affect conflicts — re-annotate every stored
      // schedule against the updated resources (preserves prior moves/edits).
      for (const [pid, schedule] of mockScheduleStore) {
        const { blocks, conflicts } = annotateConflicts(schedule.blocks, mockResourceState)
        mockScheduleStore.set(pid, { ...schedule, blocks, conflicts })
      }
      return Promise.resolve(mockResourceState.map((r) => ({ ...r })))
    }
    return request<SchedulingResource[]>(`/scheduling/resources/${resourceId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  },
}
